import React, { useRef, useState } from "react";
import { Grid } from "@mui/material";

import { FormButton, SpinnerOverlay } from "components";
import { FormikSelectInput, FormikTextInput } from "components/UI/Inputs/FormikInputs";

import { Form, Formik } from "formik";
import * as yup from "yup";

import { useRecoilValue, useSetRecoilState } from "recoil";
import { snackbarData, userCredentials } from "recoilState/GlobalState";

import styles from "styles/pages/ModalActions.module.scss";
import { connectionUpd } from "services/services-mia";
import { estadosPosibles } from "../consts";

/**
 * Modificación de un usuario interno.
 *
 * Se edita el VÍNCULO con el servicio —rol, estado y mail de contacto—, no la persona:
 * nombre, apellido y documento viven en MIA y no se administran desde este panel. Por
 * eso se muestran deshabilitados, para poder confirmar sobre quién se está operando.
 *
 * Los valores salen de la FILA: la lista ya los trae y no hace falta un Get propio.
 */
export default function UpdateModal({ roles, row, updateList, closeModal }) {
    const setSnackBarInfo = useSetRecoilState(snackbarData);
    const credentials = useRecoilValue(userCredentials);
    const formikRef = useRef();
    const [inRequest, setInRequest] = useState(false);

    // Desde un estado solo se puede pasar a los que su transición permite: una baja,
    // por ejemplo, no vuelve atrás.
    const estados = estadosPosibles(row?.estadoCod);

    const guardar = async (values) => {
        setInRequest(true);
        const respuesta = await connectionUpd(
            row.id,
            values.estado,
            values.email,
            values.rol,
            credentials
        );
        setInRequest(false);

        if (respuesta?.status?.code !== 1) {
            setSnackBarInfo({
                message: respuesta?.status?.errmsg || "No se pudo modificar el usuario",
                severity: "error",
                open: true,
            });
            return;
        }

        setSnackBarInfo({
            message: "Usuario modificado correctamente",
            severity: "success",
            open: true,
        });
        formikRef.current.resetForm();
        updateList();
        closeModal();
    };

    return (
        <Formik
            innerRef={formikRef}
            initialValues={{
                fullName: row?.fullName ?? "",
                document: row?.document ?? "",
                email: row?.email ?? "",
                rol: row?.rolCod ?? "",
                estado: row?.estadoCod ?? "",
            }}
            validationSchema={yup.object({
                email: yup
                    .string()
                    .email("Debe ingresar un email válido")
                    .required("Debe ingresar el email"),
                rol: yup.string().required("Debe seleccionar un rol"),
                estado: yup.string().required("Debe seleccionar un estado"),
            })}
            onSubmit={(values) => guardar(values)}
        >
            <Form className={styles.inputs} noValidate>
                <div className={styles["column"]} style={{ width: "100%" }}>
                    <Grid
                        container
                        rowSpacing={0}
                        columnSpacing={2}
                        sx={{
                            width: "100%",
                            "& .MuiFormControl-root": { marginTop: 0, marginBottom: 0 },
                        }}
                    >
                        {/* Identidad: solo para confirmar sobre quién se opera. */}
                        <Grid item xs={12}>
                            <FormikTextInput
                                name="fullName"
                                labelText="Apellido y nombre"
                                readOnly
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormikTextInput name="document" labelText="Documento" readOnly />
                        </Grid>

                        <Grid item xs={12}>
                            <FormikTextInput
                                name="email"
                                labelText="Email"
                                placeholder="ejemplo@mail.com"
                                inputProps={{ maxLength: 100 }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormikSelectInput name="rol" labelText="Rol">
                                <option value="" disabled>
                                    Seleccione
                                </option>
                                {roles.map(({ rolCod, rolNombre }) => (
                                    <option key={rolCod} value={rolCod}>
                                        {rolNombre}
                                    </option>
                                ))}
                            </FormikSelectInput>
                        </Grid>
                        <Grid item xs={12}>
                            <FormikSelectInput name="estado" labelText="Estado">
                                <option value="" disabled>
                                    Seleccione
                                </option>
                                {estados.map(({ cod, label }) => (
                                    <option key={cod} value={cod}>
                                        {label}
                                    </option>
                                ))}
                            </FormikSelectInput>
                        </Grid>
                    </Grid>

                    <FormButton
                        inRequest={inRequest}
                        newFormData={{ button: "Modificar" }}
                        onCloseModal={closeModal}
                    />
                    {inRequest && <SpinnerOverlay />}
                </div>
            </Form>
        </Formik>
    );
}
